import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { MdLock } from 'react-icons/md'
import Modal from 'react-responsive-modal'
import moment from 'moment'
import styles from './styles.module.scss'
import { createNotification } from '../notifications/notifications'
import { getCertGrid, saveCertGrade, closeCertSheet } from '../../actions/certification'
import {
  EXAM_KIND,
  INTERMEDIATE_CERTIFICATION_ACCESS,
  FINAL_CERTIFICATION_ACCESS,
} from '../../utils/const'

const GRADE_LIST_ID = 'certGradeValues'

const gradeKey = (mid, jcid) => `${mid}_${jcid}`

const formatAvg = avg => (typeof avg === 'number' ? avg.toFixed(2).replace('.', ',') : '')

// Редактируемая ячейка оценки: ввод с клавиатуры, выбор из списка (datalist),
// очистка. Блокируется на время сохранения, при ошибке возвращает прежнее
// значение (откат делает onSave → false).
const GradeCell = ({ grade, canEdit, className, onSave }) => {
  const value = (grade && grade.grade) || ''
  const [val, setVal] = useState(value)
  const [saving, setSaving] = useState(false)

  useEffect(() => { setVal(value) }, [value])

  const commit = async () => {
    const next = val.trim()
    if (next === value) return
    setSaving(true)
    const ok = await onSave(next)
    setSaving(false)
    if (!ok) setVal(value)
  }

  if (!canEdit) {
    const display = grade ? grade.display_grade : ''
    return (
      <div className={`${className} ${display === 'н/д' ? styles.cellNd : ''}`}>
        {display}
      </div>
    )
  }

  return (
    <div className={className}>
      <input
        className={styles.gradeInput}
        value={val}
        maxLength={6}
        disabled={saving}
        list={GRADE_LIST_ID}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
      />
    </div>
  )
}

const Table = ({ examKind }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const data = useSelector(state => state.certification.data)
  const loading = useSelector(state => state.certification.loading)
  const gradeValues = useSelector(state => state.certification.filters.gradeValues)
  const teacherMid = useSelector(state => state.auth.mid)
  const accessModes = useSelector(state => state.auth.access_modes)

  // Право изменения: «Полный» по режиму либо по его администратору.
  // Иначе режим работает только на просмотр.
  const accessPairs = examKind === EXAM_KIND.FINAL
    ? FINAL_CERTIFICATION_ACCESS
    : INTERMEDIATE_CERTIFICATION_ACCESS
  const updateRight = accessPairs.some(
    ([group, name]) => accessModes?.[group]?.[name] === 'Полный'
  )

  const [selectedJcid, setSelectedJcid] = useState(null)
  const [closing, setClosing] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState(null)
  // Вся шапка (левый блок + прокручиваемая правая часть) — её смещает JS
  // при вертикальном скролле страницы.
  const headRef = useRef(null)
  // Правая часть шапки: её scrollLeft синхронизируется со скроллом тела.
  const headScrollRef = useRef(null)
  const wrapRef = useRef(null)

  // Прилипание шапки к верху окна. CSS-sticky здесь не работает: у html/body
  // в global.css задан overflow-x: hidden, что делает body scroll-контейнером
  // и ломает sticky относительно окна. Поэтому смещаем шапку transform'ом по
  // скроллу страницы; у нижнего края таблицы шапка «отлипает».
  useEffect(() => {
    const onScroll = () => {
      const wrap = wrapRef.current
      const head = headRef.current
      if (!wrap || !head) return
      const rect = wrap.getBoundingClientRect()
      const maxOffset = rect.height - head.offsetHeight
      const offset = rect.top < 0 ? Math.min(-rect.top, maxOffset) : 0
      head.style.transform = offset > 0 ? `translateY(${offset}px)` : ''
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    onScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [data])

  const params = new URLSearchParams(location.search)
  const typeId = params.get('type_id')
  const gridParams = {
    exam_kind: examKind,
    gid: params.get('gid'),
    trmid: params.get('term_id'),
    cid: params.get('cid'),
    typeids: typeId && typeId !== '0' ? typeId : '',
  }

  const students = (data && data.students) || []
  const grades = (data && data.grades) || []
  const totals = (data && data.totals_per_sheet) || []

  // Колонки сортируем по дисциплине, затем по дате, чтобы сгруппировать
  // по дисциплинам (бэк отдаёт в порядке дат).
  const columns = useMemo(() => {
    const list = [...((data && data.columns) || [])]
    return list.sort((a, b) => (
      (a.course_name || '').localeCompare(b.course_name || '')
      || moment(a.lesson_full_date, 'DD.MM.YYYY').diff(moment(b.lesson_full_date, 'DD.MM.YYYY'))
      || (a.retake || 0) - (b.retake || 0)
    ))
  }, [data])

  // Группы колонок по дисциплине (для верхней строки шапки).
  const courseGroups = useMemo(() => {
    const groups = []
    columns.forEach((column) => {
      const last = groups[groups.length - 1]
      if (last && last.cid === column.cid && last.name === column.course_name) {
        last.count += 1
      } else {
        groups.push({ cid: column.cid, name: column.course_name, count: 1 })
      }
    })
    return groups
  }, [columns])

  const gradeMap = useMemo(() => {
    const map = {}
    grades.forEach((g) => { map[gradeKey(g.mid, g.jcid)] = g })
    return map
  }, [grades])

  const totalsMap = useMemo(() => {
    const map = {}
    totals.forEach((t) => { map[t.jcid] = t })
    return map
  }, [totals])

  // Сбрасываем выбор колонки, если её больше нет в данных.
  useEffect(() => {
    if (selectedJcid && !columns.some(c => c.jcid === selectedJcid)) {
      setSelectedJcid(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  // Колонки прокручиваемой правой части; левый блок (48/300/80px) задан в CSS.
  const gridTemplate = `repeat(${columns.length}, 130px)`

  // Сохранение оценки: валидация по справочнику, запрос, тихий перезапрос
  // ведомости (обновляет средние баллы и служебные строки).
  const handleSaveGrade = async (column, student, nextGrade) => {
    let grade = ''
    if (nextGrade) {
      const match = (gradeValues || []).find(
        g => String(g.value).trim().toUpperCase() === nextGrade.toUpperCase()
      )
      if (!match) {
        createNotification('error', 'Аттестация', `Недопустимое значение оценки: «${nextGrade}»`)
        return false
      }
      grade = match.value
    }

    const result = await dispatch(saveCertGrade({
      exam_kind: examKind,
      gid: column.gid,
      cid: column.cid,
      sheid: column.sheid,
      week_id: column.week_id,
      trmid: Number(gridParams.trmid),
      trmtid: column.trmtid,
      student_mid: student.mid,
      jcid: column.jcid,
      retake: column.retake || 0,
      grade,
      teacher_mid: teacherMid,
      comment: '',
    }))
    if (!result || !result.success) return false

    await dispatch(getCertGrid(gridParams, true))
    return true
  }

  // Закрытие ведомости. При предупреждении о пустых оценках бэк возвращает
  // requires_confirmation — показываем модалку и повторяем с force = true.
  const doClose = async (force) => {
    setClosing(true)
    try {
      const result = await dispatch(closeCertSheet({
        exam_kind: examKind,
        jcid: selectedJcid,
        force,
      }))
      if (!result) return
      if (result.requires_confirmation) {
        setConfirmMessage(result.message || 'В ведомости отсутствуют оценки. Закрыть ведомость?')
        return
      }
      if (result.status === 'success') {
        createNotification('success', 'Аттестация', result.message || 'Ведомость закрыта')
        setSelectedJcid(null)
        dispatch(getCertGrid(gridParams, true))
      }
    } finally {
      setClosing(false)
    }
  }

  const onConfirmClose = () => {
    setConfirmMessage(null)
    doClose(true)
  }

  if (loading) {
    return <div className={styles.state}>Загрузка ведомости…</div>
  }
  if (!data) {
    return (
      <div className={styles.state}>
        Выберите фильтры и нажмите «Обновить», чтобы загрузить ведомость.
      </div>
    )
  }
  if (!columns.length || !students.length) {
    return <div className={styles.state}>По выбранным параметрам нет данных ведомости.</div>
  }

  const selectedColumn = columns.find(c => c.jcid === selectedJcid)
  const canClose = Boolean(selectedColumn) && !selectedColumn.closed && updateRight && !closing

  const columnClass = column => [
    column.closed ? styles.colClosed : '',
    column.jcid === selectedJcid ? styles.colSelected : '',
  ].filter(Boolean).join(' ')

  // Служебные строки разнесены по блокам: подпись — в левом, значения — в правом.
  const serviceLabel = label => (
    <div className={`${styles.cell} ${styles.sumCell} ${styles.serviceLabel}`}>{label}</div>
  )

  const serviceValues = (label, render) => columns.map(column => (
    <div
      key={`${label}-${column.jcid}`}
      className={`${styles.cell} ${styles.sumCell} ${columnClass(column)}`}
    >
      {render(totalsMap[column.jcid])}
    </div>
  ))

  return (
    <>
      <datalist id={GRADE_LIST_ID}>
        {(gradeValues || [])
          .filter(g => String(g.value || '').trim())
          .map(g => <option key={g.value} value={g.value}>{g.text || ''}</option>)}
      </datalist>

      <div className={styles.tableWrap} ref={wrapRef}>
        {/* шапка прилипает к верху окна, пока таблица в зоне видимости */}
        <div className={styles.head} ref={headRef}>
          <div className={styles.headLeft}>
            <div className={styles.corner} />
            <div className={styles.headCell}>№</div>
            <div className={`${styles.headCell} ${styles.headName}`}>ФИО</div>
            <div className={styles.headCell}>Ср. балл</div>
          </div>

          <div className={styles.headScroll} ref={headScrollRef}>
            <div className={styles.tableHead} style={{ gridTemplateColumns: gridTemplate }}>
              {/* строка 1: дисциплины */}
              {courseGroups.map((group, index) => (
                <div
                  key={`grp-${group.cid}-${index}`}
                  className={styles.groupCell}
                  style={{ gridColumn: `span ${group.count}` }}
                  title={group.name}
                >
                  <span className={styles.groupCellText}>{group.name}</span>
                </div>
              ))}

              {/* строка 2: ведомости */}
              {columns.map(column => (
                <button
                  key={`h-${column.jcid}`}
                  type="button"
                  className={`${styles.headCell} ${styles.colHead} ${columnClass(column)}`}
                  onClick={() => setSelectedJcid(column.jcid === selectedJcid ? null : column.jcid)}
                  title={column.closed ? 'Ведомость закрыта' : 'Выбрать ведомость'}
                >
                  <span className={styles.typeBadge}>
                    {column.short_type}
                    {column.retake > 0 && ` · пересдача ${column.retake}`}
                    {column.retake < 0 && ' · досрочная'}
                  </span>
                  <span className={styles.headSheet}>
                    {column.closed && <MdLock size={14} />}
                    {column.jnumber ? `Вед. №${column.jnumber}` : 'Ведомость'}
                  </span>
                  <span className={styles.headDate}>{column.lesson_full_date}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {/* закреплённые колонки */}
          <div className={styles.bodyLeft}>
            {students.map((student, index) => (
              <React.Fragment key={`s-${student.mid}`}>
                <div className={styles.cell}>{index + 1}</div>
                <div className={`${styles.cell} ${styles.nameCell}`}>{student.fio}</div>
                <div className={`${styles.cell} ${styles.avgCell}`}>{formatAvg(student.avg)}</div>
              </React.Fragment>
            ))}
            {serviceLabel('Кол-во оценок')}
            {serviceLabel('Ср. балл')}
            {serviceLabel('Кол-во отсутствующих')}
          </div>

          {/* прокручиваемая часть: оценки */}
          <div
            className={styles.tableScroll}
            onScroll={(e) => {
              if (headScrollRef.current) headScrollRef.current.scrollLeft = e.target.scrollLeft
            }}
          >
            <div className={styles.table} style={{ gridTemplateColumns: gridTemplate }}>
              {students.map(student => columns.map((column) => {
                const grade = gradeMap[gradeKey(student.mid, column.jcid)]
                const canEdit = updateRight
                  && column.can_edit
                  && Boolean(grade)
                  && grade.has_dopusk
                  && !grade.blocked
                  && !grade.closed
                return (
                  <GradeCell
                    key={`g-${student.mid}-${column.jcid}`}
                    grade={grade}
                    canEdit={canEdit}
                    className={`${styles.cell} ${columnClass(column)} ${grade && grade.blocked ? styles.cellBlocked : ''}`}
                    onSave={next => handleSaveGrade(column, student, next)}
                  />
                )
              }))}

              {/* служебные строки */}
              {serviceValues('Кол-во оценок', t => (t ? t.grades_count : 0))}
              {serviceValues('Ср. балл', t => formatAvg(t && t.avg_grade))}
              {serviceValues('Кол-во отсутствующих', t => (t ? t.absent_count : 0))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => doClose(false)}
          disabled={!canClose}
          title={!selectedColumn
            ? 'Выберите ведомость, кликнув по заголовку колонки'
            : undefined}
        >
          Закрыть ведомость
        </button>
      </div>

      <Modal
        open={Boolean(confirmMessage)}
        onClose={() => setConfirmMessage(null)}
        center
        classNames={{ modal: styles.confirmModal }}
      >
        <p className={styles.confirmText}>{confirmMessage}</p>
        <div className={styles.confirmButtons}>
          <button type="button" className={styles.actionButton} onClick={onConfirmClose}>
            Закрыть ведомость
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
            onClick={() => setConfirmMessage(null)}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </>
  )
}

export default Table
